import ApiSmile from '@/api/api-smile';
import PageData from '@/types/page-data';
import LoadingUtil from '@/util/loading-util';
import MessageUtil from '@/util/message-util';
import React, {useEffect, useState} from 'react';
import PageParam from '@/types/page-param';
import PageContainer from '@/component/page-container';
import {ReportData} from '../types';
import {Button, CheckboxOptionType, Input, Modal, Table} from 'antd';
import {HoldState, RecommendState, TagColor} from '@/model/const';
import CheckBoxFilter from '@/component/checkbox-filter';
import FundSummary from '../component/fund-summary';
import ReportTable from '../component/report-table';
import useDeviceFeature from '@/hooks/use-device-feature';
import ReportCard from '../component/report-card';

const starFilterOption: CheckboxOptionType[] = [
    {
        label: '加星',
        value: '1',
    },
    {
        label: '无星',
        value: '0',
    },
];

const getTagColorStyle = (color: string) => {
    return {
        backgroundColor: color,
        width: '32px',
        height: '12px',
        borderRadius: '2px',
        border: 'solid 0.5px #5C5E62',
    };
};

const colorFilterOption: CheckboxOptionType[] = TagColor.map((item, index) => ({
    label: <div style={getTagColorStyle(item)} />,
    value: `${index}`,
}));

const recommendFilterOption: CheckboxOptionType[] = [
    {
        label: '买入',
        value: RecommendState.BUY,
    },
    {
        label: '卖出',
        value: RecommendState.SELL,
    },
    {
        label: '未知',
        value: 'UNKNOWN',
    },
];

const holdFilterOption: CheckboxOptionType[] = [
    {
        label: '持有',
        value: HoldState.BOUGHT,
    },
    {
        label: '清仓',
        value: HoldState.SOLD,
    },
    {
        label: '未操作',
        value: 'UNKNOWN',
    },
];

interface QueryFilter {
    recommend?: string[];
    hold?: string[];
    star?: string;
    color?: string;
    search?: string;
}

interface RawQueryFilter {
    recommend?: string[];
    hold?: string[];
    star?: string[];
    color?: string[];
    search?: string;
}

type FilterCategory = {
    category: 'search' | 'star' | 'composed';
};

type PhoneFilterCategory = {
    category: 'star' | 'composed';
};

const getQueryFilter = (raw: RawQueryFilter) => {
    const getFilter = (array?: string[]) => {
        if (!array) return undefined;
        if (array.length === 1 && array[0] === 'ALL') {
            return undefined;
        }
        return array;
    };

    const getSingleFilter = (array?: string[]) => {
        if (!array) return undefined;
        if (array.length === 1 && array[0] === 'ALL') {
            return undefined;
        }
        return array.length >= 1 ? array[0] : undefined;
    };

    return {
        recommend: getFilter(raw.recommend),
        hold: getFilter(raw.hold),
        star: getSingleFilter(raw.star),
        color: getSingleFilter(raw.color),
        search: (raw.search || '').trim() || undefined,
    };
};

const InitRawQueryFilter: RawQueryFilter = {
    recommend: [RecommendState.BUY],
    hold: [HoldState.SOLD, 'UNKNOWN'],
    star: ['ALL'],
    color: ['0'], // default green
    search: '',
};

const ReportList = () => {
    const [data, setData] = useState<PageData<ReportData>>();
    const [pageParam, setPageParam] = useState<PageParam>({page: 0, size: 10});
    const [rawQueryFilter, setRawQueryFilter] = useState<RawQueryFilter>(InitRawQueryFilter);
    const [queryFilter, setQueryFilter] = useState<QueryFilter>(getQueryFilter(rawQueryFilter));
    const [expandedRecommendRowKeys, setExpandRecommendRowKeys] = useState<number[]>([]);
    const [expandedHoldRowKeys, setExpandHoldRowKeys] = useState<number[]>([]);
    const {isPhone} = useDeviceFeature();
    const [phoneFilterCategory, setPhoneFilterCategory] = useState<PhoneFilterCategory>();

    useEffect(() => {
        const load = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.getReportList(pageParam, queryFilter);
            if (response?.data?.code === 0) {
                setData(response.data.data);
                if (!isPhone) {
                    const expand = response.data.data.content.map((item: any) => item.id);
                    setExpandHoldRowKeys(expand);
                }
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        load();
    }, [pageParam, queryFilter, isPhone]);

    const handleStarFilterClick = () => {
        setPhoneFilterCategory({category: 'star'});
    };

    const handleComposedFilterClick = () => {
        setPhoneFilterCategory({category: 'composed'});
    };

    const renderPhoneFilter = () => {
        return (
            <div className="mb-2">
                <div className="flex items-center">
                    <Input
                        placeholder="搜索"
                        value={rawQueryFilter.search}
                        onChange={(e) => setRawQueryFilter((old) => ({...old, search: e.target.value}))}
                        allowClear
                        onPressEnter={() => handleFirstButtonClick({category: 'search'})}
                    />
                    <Button className="ml-2" onClick={() => handleFirstButtonClick({category: 'search'})}>
                        {'搜索'}
                    </Button>
                </div>
                <div className="flex items-center mt-2">
                    <Button className="flex-1" onClick={handleStarFilterClick}>
                        {'加星筛选'}
                    </Button>
                    <Button className="ml-2 flex-1" onClick={handleComposedFilterClick}>
                        {'综合筛选'}
                    </Button>
                </div>
            </div>
        );
    };

    const handleFirstButtonClick = (row: FilterCategory) => {
        const all: RawQueryFilter = {
            star: ['ALL'],
            recommend: ['ALL'],
            hold: ['ALL'],
            color: ['ALL'],
            search: '',
        };
        let raw: RawQueryFilter | undefined;
        if (row.category === 'search') {
            raw = {
                ...all,
                search: rawQueryFilter.search,
            };
        } else if (row.category === 'star') {
            raw = {
                ...all,
                star: rawQueryFilter.star,
            };
        } else if (row.category === 'composed') {
            raw = {
                ...rawQueryFilter,
                star: ['ALL'],
                search: '',
            };
        }
        if (raw) {
            setRawQueryFilter(raw);
            setPageParam({...pageParam, page: 0});
            setQueryFilter(getQueryFilter(raw));
        }
    };

    const handleClearTagStar = () => {
        const clear = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.updateInfoStar({
                code: '0',
                star: 0,
            });
            if (response?.data?.code === 0) {
                handleResetComposedFilter();
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        Modal.confirm({
            title: '确定清除所有星标吗？',
            onOk: clear,
        });
    };

    const handleResetComposedFilter = () => {
        const raw = {
            ...InitRawQueryFilter,
        };
        setRawQueryFilter(raw);
        setPageParam({...pageParam, page: 0});
        setQueryFilter(getQueryFilter(raw));
    };

    const handleSecondButtonClick = (row: FilterCategory) => {
        if (row.category === 'star') {
            handleClearTagStar();
        } else if (row.category === 'composed') {
            handleResetComposedFilter();
        }
    };

    const renderTableFilter = () => {
        return (
            <Table
                showHeader={false}
                className="mb-4"
                columns={[
                    {
                        dataIndex: 'filters',
                        key: 'filters',
                        render: (_: string, row: FilterCategory) => {
                            if (row.category === 'search') {
                                return (
                                    <Input
                                        placeholder="搜索"
                                        value={rawQueryFilter.search}
                                        onChange={(e) => setRawQueryFilter((old) => ({...old, search: e.target.value}))}
                                        allowClear
                                        onPressEnter={() => handleFirstButtonClick(row)}
                                    />
                                );
                            } else if (row.category === 'star') {
                                return (
                                    <CheckBoxFilter
                                        title="星标状态："
                                        options={starFilterOption}
                                        value={rawQueryFilter.star}
                                        onChange={(checked) => setRawQueryFilter((old) => ({...old, star: checked as string[]}))}
                                        single
                                    />
                                );
                            } else if (row.category === 'composed') {
                                return (
                                    <div>
                                        <CheckBoxFilter
                                            title="颜色状态："
                                            options={colorFilterOption}
                                            value={rawQueryFilter.color}
                                            onChange={(checked) => setRawQueryFilter((old) => ({...old, color: checked as string[]}))}
                                            single
                                        />
                                        <CheckBoxFilter
                                            title="推荐状态："
                                            options={recommendFilterOption}
                                            value={rawQueryFilter.recommend}
                                            onChange={(checked) => setRawQueryFilter((old) => ({...old, recommend: checked as string[]}))}
                                        />
                                        <CheckBoxFilter
                                            title="持有状态："
                                            options={holdFilterOption}
                                            value={rawQueryFilter.hold}
                                            onChange={(checked) => setRawQueryFilter((old) => ({...old, hold: checked as string[]}))}
                                        />
                                    </div>
                                );
                            }
                        },
                    },
                    {
                        dataIndex: 'action',
                        key: 'action',
                        render: (_: string, row: FilterCategory) => (
                            <div className="flex items-center space-x-4">
                                <Button onClick={() => handleFirstButtonClick(row)}>{row.category === 'search' ? '搜索' : '刷新'}</Button>
                                {row.category !== 'search' && (
                                    <Button onClick={() => handleSecondButtonClick(row)}>{row.category === 'star' ? '清除' : '重置'}</Button>
                                )}
                            </div>
                        ),
                    },
                ]}
                pagination={false}
                bordered
                dataSource={[{category: 'search'}, {category: 'star'}, {category: 'composed'}]}
            />
        );
    };

    const onStarClick = async (record: ReportData, star: number) => {
        LoadingUtil.show();
        const response: any = await ApiSmile.updateInfoStar({
            code: record.code,
            star,
        });
        if (response?.data?.code === 0) {
            record.tagStar = star;
            setData(data);
        } else {
            const msg = response?.data?.msg || '网络请求失败';
            MessageUtil.error(msg);
        }
        LoadingUtil.hide();
    };

    const onRowExpand = (key: 'recommend' | 'hold', expanded: boolean, record: ReportData) => {
        const data = key === 'recommend' ? expandedRecommendRowKeys : expandedHoldRowKeys;
        const func = key === 'recommend' ? setExpandRecommendRowKeys : setExpandHoldRowKeys;
        if (expanded) {
            const keys = [...data];
            keys.push(record.id);
            func(keys);
        } else {
            const keys = data.filter((item) => item !== record.id);
            func(keys);
        }
    };

    return (
        <PageContainer>
            <FundSummary />
            {isPhone ? renderPhoneFilter() : renderTableFilter()}
            {isPhone ? (
                <ReportCard
                    data={data}
                    expandedRowKeys={{recommend: expandedRecommendRowKeys, hold: expandedHoldRowKeys}}
                    onRowExpand={onRowExpand}
                    onStarClick={onStarClick}
                    setPageParam={setPageParam}
                />
            ) : (
                <ReportTable
                    data={data}
                    expandedRowKeys={{recommend: expandedRecommendRowKeys, hold: expandedHoldRowKeys}}
                    onRowExpand={onRowExpand}
                    onStarClick={onStarClick}
                    setPageParam={setPageParam}
                />
            )}
            <Modal
                visible={!!phoneFilterCategory}
                okType={'default'}
                onCancel={() => setPhoneFilterCategory(undefined)}
                footer={
                    <div className="flex items-center space-x-4 justify-end">
                        <Button
                            onClick={() => {
                                if (phoneFilterCategory) {
                                    handleFirstButtonClick(phoneFilterCategory);
                                }
                                setPhoneFilterCategory(undefined);
                            }}
                        >
                            {'刷新'}
                        </Button>
                        <Button
                            onClick={() => {
                                if (phoneFilterCategory) {
                                    handleSecondButtonClick(phoneFilterCategory);
                                }
                                setPhoneFilterCategory(undefined);
                            }}
                        >
                            {phoneFilterCategory?.category === 'star' ? '清除' : '重置'}
                        </Button>
                    </div>
                }
            >
                <div>
                    {phoneFilterCategory?.category === 'star' ? (
                        <CheckBoxFilter
                            title="星标状态："
                            options={starFilterOption}
                            value={rawQueryFilter.star}
                            onChange={(checked) => setRawQueryFilter((old) => ({...old, star: checked as string[]}))}
                            single
                        />
                    ) : (
                        <div>
                            <CheckBoxFilter
                                title="颜色状态："
                                options={colorFilterOption}
                                value={rawQueryFilter.color}
                                onChange={(checked) => setRawQueryFilter((old) => ({...old, color: checked as string[]}))}
                                single
                            />
                            <CheckBoxFilter
                                title="推荐状态："
                                options={recommendFilterOption}
                                value={rawQueryFilter.recommend}
                                onChange={(checked) => setRawQueryFilter((old) => ({...old, recommend: checked as string[]}))}
                            />
                            <CheckBoxFilter
                                title="持有状态："
                                options={holdFilterOption}
                                value={rawQueryFilter.hold}
                                onChange={(checked) => setRawQueryFilter((old) => ({...old, hold: checked as string[]}))}
                            />
                        </div>
                    )}
                </div>
            </Modal>
        </PageContainer>
    );
};

export default ReportList;
